import { Fragment, useState } from 'react';
import colorTokens from '../../../constants/design_system/colors';
import { SprintStatusLabel, SprintStatusOptions, SprintStatusType } from '../SprintStatusLabel';
import BaseDropdown from './BaseDropdown';
import './ItemDropdown.scss';
import NoDragWrapper from './NoDragWrapper';

export type SprintStatusDropdownProps = {
  sprintStatus: SprintStatusType | null;
  sprintStatuses: SprintStatusType[];
  onSelect?: (sprintStatus?: SprintStatusType) => void;
  onBlur: () => void;
  disabled?: boolean;
  size: 'small' | 'medium' | 'large';
  hideOutline?: boolean;
  defaultOpen?: boolean;
  className?: string;
  position?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'topCenter' | 'topLeft' | 'topRight';
};

const SprintStatusDropdown = (props: SprintStatusDropdownProps) => {
  const [isDropdownOpen, setDropdownOpen] = useState(props.defaultOpen || false);
  const [scrollKey, setScrollKey] = useState(0);

  // Sort sprintStatuses according to SprintStatusOptions order
  const sortedSprintStatuses = [...props.sprintStatuses].sort((a, b) => {
    const indexA = SprintStatusOptions.indexOf(a);
    const indexB = SprintStatusOptions.indexOf(b);
    return indexA - indexB;
  });

  // Stages are visually grouped into three sections separated by dividers:
  // [triage] / [backlog/planning/sprint/kanban] / [archive].
  const triageGroup = sortedSprintStatuses.filter((s) => s === 'triage');
  const archiveGroup = sortedSprintStatuses.filter((s) => s === 'archive');
  const middleGroup = sortedSprintStatuses.filter((s) => s !== 'triage' && s !== 'archive');
  const groups = [triageGroup, middleGroup, archiveGroup].filter((g) => g.length > 0);

  const onSelect = (sprintStatus: SprintStatusType) => {
    if (props.onSelect) {
      props.onSelect(sprintStatus);
    }
    isDropdownOpen && setDropdownOpen(false);
  };

  const renderItem = (sprintStatus: SprintStatusType) => {
    const isSelected = sprintStatus === props.sprintStatus;
    return (
      <div
        key={sprintStatus}
        className={`w-full cursor-pointer rounded ${isSelected ? 'outline outline-2' : ''}`}
        style={isSelected ? { outlineColor: colorTokens.green[300] } : undefined}
        onClick={() => onSelect(sprintStatus)}
      >
        <SprintStatusLabel sprintStatus={sprintStatus} component="dropdown" />
      </div>
    );
  };

  return (
    <NoDragWrapper className="h-full">
      <BaseDropdown
        disableTransition
        trigger={['click']}
        open={isDropdownOpen}
        disabled={props.disabled}
        placement={props.position || 'bottomLeft'}
        onOpenChange={(open) => {
          if (open) setScrollKey((k) => k + 1);
          setDropdownOpen(open);
          if (!open && props.onBlur) {
            props.onBlur();
          }
        }}
        dropdownRender={() => (
          <div
            key={scrollKey}
            className="flex flex-col items-start justify-start gap-2 bg-brand-white shadow-md rounded-lg p-2 max-h-[500px] overflow-y-auto"
          >
            {groups.map((group, groupIdx) => (
              <Fragment key={groupIdx}>
                {groupIdx > 0 && <div className="w-full h-px bg-neutral-200" />}
                {group.map(renderItem)}
              </Fragment>
            ))}
          </div>
        )}
      >
        <div
          className={`${props.className ?? 'h-full'} flex items-center cursor-pointer`}
          style={{ outline: isDropdownOpen && !props.hideOutline ? `2px solid ${colorTokens.green[300]}` : 'none' }}
        >
          {props.sprintStatus ? (
            <div className="relative parent-task">
              <SprintStatusLabel sprintStatus={props.sprintStatus} component="dropdown" />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-neutral-900 text-sm font-normal">Stage</div>
            </div>
          )}
        </div>
      </BaseDropdown>
    </NoDragWrapper>
  );
};

export default SprintStatusDropdown;
